// Share invitations (4.10.55, 2026-10-26). Sharing itself is unchanged: a
// guide's owner writes "uid:R" / "uid:C" / "uid:M" into guide_info.access.
// What's new is the recipient's side — their own private record
// (user_sharing/{uid}, readable and writable only by them):
//   accepted: guide ids they accepted (shown in Library → Shared)
//   hidden:   guide ids they declined, removed or reported (never shown)
//   blocked:  user ids whose guides they never want to see
//   initialized: true once their pre-existing shares were accepted for them
// A guide shared with them that is in none of these is an invitation.
// Kept identical in andiamo-places/src/Common/shareInvites.js.

export const EMPTY_SHARING_STATE = { accepted: [], hidden: [], blocked: [], initialized: false };

// The level a person was given personally ("R" | "C" | "M"), or null when
// the guide isn't shared with them by name (e.g. only "All users can …").
export const personalShareLevel = (access, uid) => {
  if (!Array.isArray(access) || !uid) return null;
  let best = null;
  const rank = { R: 1, C: 2, M: 3 };
  access.forEach((a) => {
    if (typeof a !== "string" || !a.startsWith(`${uid}:`)) return;
    const level = a.slice(uid.length + 1);
    if (rank[level] && (!best || rank[level] > rank[best])) best = level;
  });
  return best;
};

export const shareLevelLabel = (level) => (level === "M" ? "can edit" : level === "C" ? "can copy" : level === "R" ? "can read" : "");

export const normalizeSharingState = (state) => ({
  accepted: Array.isArray(state?.accepted) ? state.accepted : [],
  hidden: Array.isArray(state?.hidden) ? state.hidden : [],
  blocked: Array.isArray(state?.blocked) ? state.blocked : [],
  initialized: state?.initialized === true,
});

// guides: [{ id, data: guide_info }]. Returns the guides shared with uid by
// name (not their own), split into invitations and accepted; hidden guides
// and guides from blocked people are left out of both.
export const splitSharedGuides = (guides, uid, state) => {
  const s = normalizeSharingState(state);
  const invitations = [];
  const accepted = [];
  (guides || []).forEach((g) => {
    const info = g?.data || {};
    if (!uid || info.user_id === uid) return;
    if (!personalShareLevel(info.access, uid)) return;
    if (s.hidden.includes(g.id) || s.blocked.includes(info.user_id)) return;
    if (s.accepted.includes(g.id)) accepted.push(g);
    else invitations.push(g);
  });
  return { invitations, accepted };
};

// First run of the new version: everything already shared with the person
// counts as accepted, so nobody opens the Library to a pile of invitations.
export const initialSharingState = (guides, uid) => {
  const { invitations, accepted } = splitSharedGuides(guides, uid, EMPTY_SHARING_STATE);
  return { accepted: [...invitations, ...accepted].map((g) => g.id), hidden: [], blocked: [], initialized: true };
};

// Library → Shared, "Declined and blocked" (2026-10-29): so a decline or a
// block can be undone. declined: guides still shared with uid by name that
// they declined, removed or reported; blocked: [{ uid, name, guides }] for
// each person they blocked (name from a guide that person still shares with
// them, else ""). A blocked person's guides are counted under the person,
// not listed again as declined.
// others (2026-11-01): hidden guides that aren't shared with uid by name — a
// reported or removed Featured or "all users" guide — read by id
// (otherHiddenIds), so those can be restored too; they're marked other: true.
export const declinedAndBlocked = (guides, uid, state, others = []) => {
  const s = normalizeSharingState(state);
  const people = {};
  s.blocked.forEach((b) => (people[b] = { uid: b, name: "", guides: 0 }));
  const declined = [];
  (guides || []).forEach((g) => {
    const info = g?.data || {};
    if (!uid || info.user_id === uid || !personalShareLevel(info.access, uid)) return;
    const person = people[info.user_id];
    if (person) {
      person.guides += 1;
      if (!person.name) person.name = info.owner_name || "";
    } else if (s.hidden.includes(g.id)) declined.push(g);
  });
  const listed = new Set(declined.map((g) => g.id));
  (others || []).forEach((g) => {
    const info = g?.data || {};
    if (!g?.id || listed.has(g.id) || !s.hidden.includes(g.id) || info.user_id === uid || s.blocked.includes(info.user_id)) return;
    listed.add(g.id);
    declined.push({ ...g, other: true });
  });
  return { declined, blocked: s.blocked.map((b) => people[b]) };
};

// The hidden guide ids the Shared list doesn't hold (not shared by name):
// read them by id for "Declined and blocked".
export const otherHiddenIds = (guides, state) => {
  const listed = new Set((guides || []).map((g) => g?.id));
  return normalizeSharingState(state).hidden.filter((id) => !listed.has(id));
};

// The same change DataBase.sharingAction makes, applied to a local copy so
// the screen updates at once. action: "accept" | "hide" | "restore" (a
// declined guide becomes an invitation again) | "block" | "unblock".
export const applySharingAction = (state, action, id) => {
  const s = normalizeSharingState(state);
  const without = (list) => list.filter((x) => x !== id);
  if (action === "accept") return { ...s, accepted: [...without(s.accepted), id], hidden: without(s.hidden) };
  if (action === "hide") return { ...s, hidden: [...without(s.hidden), id], accepted: without(s.accepted) };
  if (action === "restore") return { ...s, hidden: without(s.hidden) };
  if (action === "block") return { ...s, blocked: [...without(s.blocked), id] };
  if (action === "unblock") return { ...s, blocked: without(s.blocked) };
  return s;
};

// A guide the person hid (declined, removed, reported) or whose owner they
// blocked — left out of every Library list, Featured included (2026-10-29).
export const hiddenFromLibrary = (g, state) => {
  const s = normalizeSharingState(state);
  return s.hidden.includes(g?.id) || s.blocked.includes(g?.data?.user_id);
};

export const REPORT_REASONS = ["Sexual content", "Violence or hate", "Spam or scam", "Other"];

// One report per person per guide (2026-10-29): the report's id is the guide
// plus the reporter, and the Firestore rule refuses a second one (reporters
// can't change a report). The rule also checks the reason, the fields and
// their lengths, and that the guide exists and isn't the reporter's own —
// keep these limits the same as docs/FirebaseRulesPhase4.txt.
export const reportDocId = (guideInfoId, uid) => `${guideInfoId}_${uid}`;
export const REPORT_LIMITS = { details: 1000, guide_name: 300, owner_name: 200, reporter_email: 320 };
export const reportFields = (r) => ({
  guide_info_id: String(r.guide_info_id || ""),
  guide_name: String(r.guide_name || "").slice(0, REPORT_LIMITS.guide_name),
  owner_uid: String(r.owner_uid || ""),
  owner_name: String(r.owner_name || "").slice(0, REPORT_LIMITS.owner_name),
  reporter_uid: String(r.reporter_uid || ""),
  reporter_email: String(r.reporter_email || "").slice(0, REPORT_LIMITS.reporter_email),
  reason: REPORT_REASONS.includes(r.reason) ? r.reason : "Other",
  details: String(r.details || "").trim().slice(0, REPORT_LIMITS.details),
});

// Admin Dashboard → Reports: one row per guide, open first, most reported
// first; each reporter's total, so someone who reports a lot stands out.
export const groupReports = (reports) => {
  const byGuide = new Map();
  const perReporter = {};
  (reports || []).forEach((r) => {
    const key = r.guide_info_id || r.id;
    if (!byGuide.has(key)) byGuide.set(key, { guide_info_id: r.guide_info_id, guide_name: r.guide_name, owner: r.owner_name || r.owner_uid, reports: [] });
    byGuide.get(key).reports.push(r);
    perReporter[r.reporter_uid] = (perReporter[r.reporter_uid] || 0) + 1;
  });
  const rows = [...byGuide.values()].map((g) => {
    g.reports.sort((a, b) => (a.created < b.created ? 1 : -1));
    g.open = g.reports.filter((r) => r.status === "open").length;
    g.latest = g.reports[0]?.created || "";
    g.reasons = [...new Set(g.reports.map((r) => r.reason))];
    return g;
  });
  rows.sort((a, b) => (b.open > 0) - (a.open > 0) || b.reports.length - a.reports.length || (a.latest < b.latest ? 1 : -1));
  return { rows, perReporter };
};
